import { useState, useEffect } from 'react';

interface Artwork {
  id: string;
  title: string;
  category: string;
  format: string;
  canvas: string;
  medium: string;
  size: string;
  price: number;
  image: string;
  description: string;
  seo_description?: string;
  technique: string;
  inspiration: string;
  year: number;
  status: string;
  tags: string[];
  vk_link: string;
  website_link: string;
}

interface ArtworksData {
  featured: Artwork[];
  gallery: Artwork[];
}

export const useArtworks = () => {
  const [artworks, setArtworks] = useState<ArtworksData>({ featured: [], gallery: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadArtworks = async () => {
      try {
        setLoading(true);
        console.log('🎨 useArtworks: Starting to load artworks...');
        
        // Проверяем кеш (1 минута для быстрого обновления)
        const cached = sessionStorage.getItem('artworks_cache');
        const cacheTime = sessionStorage.getItem('artworks_cache_time');
        const now = Date.now();
        
        // Кеш на 1 минуту
        if (cached && cacheTime && (now - parseInt(cacheTime)) < 60000) {
          const cachedData = JSON.parse(cached);
          setArtworks(cachedData);
          setLoading(false);
          setError(null);
          console.log('✅ Loaded artworks from cache:', {
            featured: cachedData.featured.length,
            gallery: cachedData.gallery.length,
            cacheAge: Math.round((now - parseInt(cacheTime)) / 1000) + 's'
          });
          return;
        }
        
        console.log('🌐 Cache expired or missing, fetching from API...');
        
        // Используем основной API endpoint
        const response = await fetch('https://w-wagner.art/get-artworks.php', {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'Accept': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Ошибка загрузки данных');
        }
        
        // get-artworks.php возвращает все товары в массиве data
        if (result.data && Array.isArray(result.data)) {
          // Разделяем на featured и обычные
          const featured = result.data.filter(item => item.featured === true || item.featured === 1 || item.featured === '1');
          const gallery = result.data.filter(item => !item.featured || item.featured === false || item.featured === 0 || item.featured === '0');
          
          const artworksData = {
            featured: featured.length > 0 ? featured : result.data.slice(0, 4),
            gallery: gallery.length > 0 ? gallery : result.data
          };
          
          setArtworks(artworksData);
          console.log('✅ Loaded artworks from get-artworks.php:', {
            total: result.data.length,
            featured: artworksData.featured.length,
            gallery: artworksData.gallery.length
          });
          console.log('📋 Sample artwork data:', artworksData.gallery.slice(0, 2).map(a => ({ 
            id: a.id, 
            title: a.title, 
            website_link: a.website_link,
            image: a.image
          })));
          
          // Кешируем результат
          sessionStorage.setItem('artworks_cache', JSON.stringify(artworksData));
          sessionStorage.setItem('artworks_cache_time', now.toString());
        } else {
          throw new Error('Некорректная структура данных API');
        }
        setError(null);
        
      } catch (err) {
        console.error('Failed to load artworks from database:', err);
        setError('Не удалось загрузить данные из базы данных');
        setArtworks({ featured: [], gallery: [] });
      } finally {
        setLoading(false);
      }
    };

    loadArtworks();
  }, []);

  const refreshArtworks = async () => {
    try {
      setLoading(true);
      
      // Очищаем кеш перед обновлением
      sessionStorage.removeItem('artworks_cache');
      sessionStorage.removeItem('artworks_cache_time');
      
      // Используем основной API endpoint
      const response = await fetch('https://w-wagner.art/get-artworks.php', {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Ошибка загрузки данных');
      }
      
      // get-artworks.php возвращает все товары в массиве data
      if (result.data && Array.isArray(result.data)) {
        // Разделяем на featured и обычные
        const featured = result.data.filter(item => item.featured === true || item.featured === 1 || item.featured === '1');
        const gallery = result.data.filter(item => !item.featured || item.featured === false || item.featured === 0 || item.featured === '0');
        
        const artworksData = {
          featured: featured.length > 0 ? featured : result.data.slice(0, 4),
          gallery: gallery.length > 0 ? gallery : result.data
        };
        
        setArtworks(artworksData);
        
        // Обновляем кеш
        sessionStorage.setItem('artworks_cache', JSON.stringify(artworksData));
        sessionStorage.setItem('artworks_cache_time', Date.now().toString());
      } else {
        throw new Error('Некорректная структура данных API');
      }
      setError(null);
      
    } catch (err) {
      console.error('Failed to refresh artworks from database:', err);
      setError('Не удалось обновить данные из базы данных');
    } finally {
      setLoading(false);
    }
  };

  return { artworks, loading, error, refreshArtworks };
};